export type UsSelfDriveBookingState =
  | "awaiting_host"
  | "accepted_payment_pending"
  | "confirmed"
  | "payment_failed"
  | "terminal"
  | "other";

/** After the booking's ID check, remaining checks are owned by us, not by the renter. */
export function hasBookingChecksInProgress(
  summary: {
    driverClearance?: import("@workspace/booking-rules").BookingDriverOverview;
    diditFieldsCurrent?: boolean;
    bookingIdVerified?: boolean;
    licenceReuploadRequested?: boolean;
    action?: "start" | "resume" | "none";
    state?: string;
    bookingMvrState?: string | null;
    internationalCheckState?: string | null;
  } | null | undefined,
): boolean {
  if (summary?.driverClearance) return ["verification_pending", "in_review"].includes(summary.driverClearance.viewer);
  return (summary?.bookingIdVerified === true || summary?.diditFieldsCurrent === true) &&
    summary.licenceReuploadRequested !== true &&
    summary.action === "none" &&
    !["complete", "failed"].includes(summary.state ?? "") &&
    !["approved", "rejected"].includes(summary.bookingMvrState ?? "") &&
    !["approved", "rejected"].includes(summary.internationalCheckState ?? "");
}

/** Keep the reservation awaiting its host while offering the assigned driver's Didit step. */
export function showPreAcceptanceDidit(input: {
  bookingState: UsSelfDriveBookingState | null;
  isRenter: boolean;
  isOwner: boolean;
  canVerify: boolean;
  summary?: {
    state: string;
    action: "start" | "resume" | "none";
    bookingState?: string;
    licenceReviewState?: "international_pending" | "international_approved";
    internationalCheckState?: "not_started" | "pending" | "approved" | "rejected";
    licenceReuploadRequested?: boolean;
  } | null;
}): boolean {
  if (input.summary?.licenceReviewState && input.summary.licenceReuploadRequested !== true) return false;
  return input.bookingState === "awaiting_host" &&
    !input.isOwner && input.canVerify &&
    input.summary?.bookingState === "awaiting_host_acceptance" &&
    (input.summary.action === "start" ||
      input.summary.action === "resume" ||
      ["in_progress", "in_review"].includes(input.summary.state));
}

export type LicenceReviewState = "international_pending" | "international_approved";

export function licenceReviewMessageKey(
  summary: {
    licenceReviewState?: LicenceReviewState;
    driverClearance?: import("@workspace/booking-rules").BookingDriverOverview;
    internationalCheckState?: "not_started" | "pending" | "approved" | "rejected";
    licenceReuploadRequested?: boolean;
  } | null | undefined,
  bookingState: UsSelfDriveBookingState | null,
): string | null {
  if (!summary?.licenceReviewState || summary.licenceReuploadRequested === true) return null;
  if (summary.internationalCheckState === "rejected") {
    return "verification.international_check_not_approved";
  }
  if (summary.licenceReviewState === "international_pending") {
    return bookingState === "awaiting_host"
      ? "verification.international_licence_pending_host"
      : "verification.international_licence_review_pending";
  }
  if (bookingState === "awaiting_host") return "verification.international_licence_approved_waiting_host";
  if (summary.driverClearance?.viewer === "approved") return "driver_clearance.approved";
  if (summary.internationalCheckState === "pending") return "driver_clearance.review";
  if (bookingState === "confirmed") return "verification.international_licence_approved_booking_pending";
  if (summary.internationalCheckState === "approved") {
    return "verification.international_check_approved_booking_pending";
  }
  return "verification.international_licence_approved_booking_pending";
}

export function bookingMvrSummaryKey(state: string | null | undefined): string | null {
  switch (String(state ?? "").trim().toLowerCase()) {
    case "pending":
    case "submitted":
    case "queued":
    case "processing":
    case "in_progress":
      return "verification.panel.phase_mvr_in_progress";
    case "review":
    case "in_review":
    case "manual_review":
    case "flagged":
      return "verification.panel.phase_mvr_review";
    case "approved":
    case "complete":
    case "completed":
      return "verification.panel.phase_approved";
    case "declined":
    case "rejected":
      return "verification.panel.phase_mvr_declined";
    case "error":
    case "failed":
      return "verification.panel.phase_error";
    default:
      return null;
  }
}

/**
 * Shared subset of detail and list responses used by this projection. Keep
 * the contract structural so compact Trips rows do not need detail-only car
 * fields merely to resolve the booking lifecycle state.
 */
type BookingProjection = {
  status?: string | null;
  booking_type?: string | null;
  rental_type?: string | null;
  approved_at?: string | null;
  source?: string | null;
  source_id?: number | string | null;
  platform_origin?: string | null;
  verification_hold?: boolean | null;
  payment_status?: string | number | null;
  cars?: { id?: number; country_code?: string | null } | null;
  verificationSummary?: {
    bookingState?: "excluded" | "awaiting_host_acceptance" | "awaiting_didit" | "awaiting_samba" | "samba_pending" | "accepted_pending" | "payment_pending" | "action_required" | "confirmed";
    paymentReady?: boolean;
    licenceReviewState?: "international_pending" | "international_approved";
    internationalCheckState?: "not_started" | "pending" | "approved" | "rejected";
  } | null;
  driverVerification?: {
    eligible?: boolean;
    required?: boolean;
    state?: string;
    bookingState?: "excluded" | "awaiting_host_acceptance" | "awaiting_didit" | "awaiting_samba" | "samba_pending" | "accepted_pending" | "payment_pending" | "action_required" | "confirmed";
    paymentReady?: boolean;
    assignedDriverIsViewer?: boolean;
  } | null;
};

/**
 * Project reservation/payment state independently from driver clearance.
 * The assigned driver may complete Didit while a request awaits its host;
 * neither Didit nor Samba delays confirmation after host acceptance and
 * successful required rental payment.
 */
export function getUsSelfDriveBookingState(
  trip: BookingProjection | null | undefined,
): UsSelfDriveBookingState | null {
  if (!trip || String(trip.cars?.country_code ?? "").trim().toUpperCase() !== "US") return null;
  const rentalType = String(trip.rental_type ?? "").trim().toLowerCase();
  if (!["driving", "self-driving", "self_drive", "self-drive", "selfdrive"].includes(rentalType)) return null;
  const status = String(trip.status ?? "").trim().toLowerCase();
  const bookingType = String(trip.booking_type ?? "").trim().toLowerCase();
  const isRequest = bookingType === "request" || bookingType === "0";
  const isInstant = bookingType === "instant" || bookingType === "1";
  const projected = trip.verificationSummary?.bookingState ?? trip.driverVerification?.bookingState;
  const paymentStatus = String(trip.payment_status ?? "").trim().toLowerCase();
  const paymentReady =
    (trip.verificationSummary?.paymentReady ?? trip.driverVerification?.paymentReady) === true ||
    Number(paymentStatus) === 4 ||
    ["paid", "succeeded", "captured", "complete", "completed"].includes(paymentStatus);

  // Payment failure wins even if an old row still carries an advanced status.
  if (["payment_failed", "failed", "failure"].includes(paymentStatus)) {
    return "payment_failed";
  }
  if (["cancelled", "declined", "expired", "no_show"].includes(status)) return "terminal";
  if (status === "completed" || status === "in_progress") return "other";

  const accepted = isInstant || trip.approved_at != null ||
    ["approved", "confirmed", "trip_confirmed"].includes(status);
  const requestAwaitingHost = status === "pending" && isRequest && trip.approved_at == null;

  // A fresh request remains awaiting its host even if the assigned driver has
  // already completed Didit. Provider progress never promotes the reservation.
  if (requestAwaitingHost) return "awaiting_host";

  // `paymentReady` is the server-attested required-payment fact (including a
  // valid pay-later authorization); persisted paid status is retained as the
  // compatibility signal for established booking records. Samba is
  // deliberately not part of this gate.
  if (accepted && paymentReady) return "confirmed";
  const imported = trip.source === "legacy_csv_import" ||
    (trip.source_id != null && String(trip.source_id).trim() !== "") ||
    ["driveshare", "ds"].includes(String(trip.platform_origin ?? "").trim().toLowerCase());
  const confirmedLifecycle = ["confirmed", "trip_confirmed"].includes(status);
  // Imported bookings were settled outside Stripe. Preserve a confirmed,
  // already-verified import even though it has no platform payment signal.
  if (imported && confirmedLifecycle) return "confirmed";
  if (projected === "awaiting_host_acceptance" && !accepted) return "awaiting_host";
  if (accepted || (projected && projected !== "excluded")) return "accepted_payment_pending";
  return "other";
}

/** Return the reservation-only state to use for status pills on US self-drive bookings. */
export function usSelfDriveReservationPillState(
  trip: BookingProjection | null | undefined,
): "awaiting_host" | "accepted_payment_pending" | "confirmed" | null {
  const status = String(trip?.status ?? "").trim().toLowerCase();
  if (status === "completed" || status === "in_progress") return null;
  const state = getUsSelfDriveBookingState(trip);
  return state === "awaiting_host" ||
    state === "accepted_payment_pending" ||
    state === "confirmed"
    ? state
    : null;
}

/** Only the assigned driver can initiate or resume the provider session. */
export function canCurrentViewerVerifyForBooking(args: {
  isOwner?: boolean;
  isRenter?: boolean;
  assignmentChoice?: "unresolved" | "renter" | "nominated" | null;
  assignedDriverIsViewer?: boolean;
  eligibleDriverIsViewer?: boolean;
  serverAction?: "start" | "resume" | "none";
}): boolean {
  if (args.isOwner === true || args.assignedDriverIsViewer === false) return false;
  return args.assignedDriverIsViewer === true ||
    args.eligibleDriverIsViewer === true ||
    args.serverAction === "start" ||
    args.serverAction === "resume";
}